import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Share2, Check, Link2, ShieldCheck, Trash2, ExternalLink } from "lucide-react";
import { api } from "../api/client";
import { Button, Field } from "./UIKit";
import { formatDate } from "../lib/fleet";

/**
 * ShareModal — Create a private share link for selected documents of ONE vehicle.
 * Also lists existing active shares for the vehicle with a "Revoke" button.
 *
 * Props:
 * - vehicle: current vehicle object (with documents[])
 * - onClose(): close the modal
 */
export function ShareModal({ vehicle, onClose }) {
  const [step, setStep] = useState("configure"); // 'configure' | 'created'
  const [selected, setSelected] = useState({});
  const [recipient, setRecipient] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [allowDownload, setAllowDownload] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [createdShare, setCreatedShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [existingShares, setExistingShares] = useState([]);

  const docs = vehicle?.documents || [];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shares?vehicle_id=${vehicle.id}`);
        setExistingShares(data.filter((s) => !s.revoked));
      } catch { /* ignore */ }
    })();
  }, [vehicle.id]);

  const toggleDoc = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const generateLink = () => {
    if (!createdShare) return "";
    return `${window.location.origin}/share/${createdShare.token}`;
  };

  const copyLink = async () => {
    const link = generateLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const nativeShare = async () => {
    const link = generateLink();
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Documents véhicule — My Fleet",
          text: `Documents de ${vehicle.brand} ${vehicle.model} (${vehicle.plate}) partagés via My Fleet.`,
          url: link,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const submit = async () => {
    if (busy) return;
    setError(null);
    if (selectedIds.length === 0) {
      setError("Sélectionnez au moins un document.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shares", {
        vehicle_id: vehicle.id,
        intervention_ids: selectedIds,
        expires_at: expiresAt || null,
        recipient_label: recipient.trim() || null,
        allow_download: allowDownload,
      });
      setCreatedShare(data);
      setStep("created");
    } catch (e) {
      setError(e?.response?.data?.detail || "Création impossible");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (sid) => {
    try {
      await api.delete(`/shares/${sid}`);
      setExistingShares((prev) => prev.filter((s) => s.id !== sid));
      toast.success("Lien révoqué");
    } catch {
      toast.error("Révocation impossible");
    }
  };

  return (
    <div
      onClick={() => !busy && onClose?.()}
      data-testid="share-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(11,25,44,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 160, padding: "20px 12px 0",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mf-anim-in"
        style={{
          width: "100%", maxWidth: 560, background: "var(--mf-bg-card)",
          borderRadius: "24px 24px 0 0", padding: 24, maxHeight: "88vh", overflowY: "auto",
          boxShadow: "var(--mf-shadow-strong)",
          paddingBottom: `calc(24px + env(safe-area-inset-bottom))`,
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "var(--mf-border)", margin: "0 auto 16px" }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>
            {step === "created" ? "Lien créé" : "Partager des documents"}
          </h2>
          <button
            onClick={onClose}
            data-testid="share-close"
            style={{
              width: 36, height: 36, borderRadius: 18, border: "none",
              background: "var(--mf-bg-muted)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: "4px 0 16px", fontSize: 13, color: "var(--mf-text-secondary)" }}>
          <ShieldCheck size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
          Lien privé, limité aux documents choisis. Aucun accès aux autres véhicules.
        </p>

        {step === "configure" ? (
          <>
            {docs.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--mf-text-secondary)", fontSize: 14 }}>
                Aucun document à partager sur ce véhicule.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.5, margin: "8px 0" }}>
                  Documents à partager
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                  {docs.map((d) => {
                    const active = !!selected[d.id];
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggleDoc(d.id)}
                        data-testid={`share-doc-${d.id}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                          borderRadius: 12, border: `1px solid ${active ? "var(--mf-brand)" : "var(--mf-border)"}`,
                          background: active ? "rgba(11,25,44,0.05)" : "var(--mf-bg-card)",
                          cursor: "pointer", textAlign: "left",
                        }}
                      >
                        <span style={{
                          width: 22, height: 22, borderRadius: 6,
                          background: active ? "var(--mf-brand)" : "var(--mf-bg-muted)",
                          border: `1.5px solid ${active ? "var(--mf-brand)" : "var(--mf-border)"}`,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>{active && <Check size={14} color="#fff" strokeWidth={3} />}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-text)" }}>{d.title}</div>
                          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>
                            {d.category_label}{d.expiration_date ? ` · Expire ${formatDate(d.expiration_date)}` : d.date ? ` · Émis ${formatDate(d.date)}` : ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <Field
                  testID="share-recipient"
                  label="Destinataire (optionnel)"
                  value={recipient}
                  onChange={setRecipient}
                  placeholder="Ex : Chauffeur Clio 5"
                />
                <Field
                  testID="share-expires-at"
                  label="Expiration (optionnel)"
                  type="date"
                  value={expiresAt}
                  onChange={setExpiresAt}
                  hint="Après cette date, le lien ne fonctionnera plus."
                />
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", background: "var(--mf-bg-muted)", borderRadius: 12, marginBottom: 8,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--mf-text)" }}>Autoriser le téléchargement</div>
                    <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
                      Sinon le destinataire ne pourra que consulter les fichiers.
                    </div>
                  </div>
                  <button
                    onClick={() => setAllowDownload((v) => !v)}
                    role="switch"
                    aria-checked={allowDownload}
                    data-testid="share-allow-download"
                    style={{
                      width: 46, height: 26, borderRadius: 999,
                      background: allowDownload ? "var(--mf-brand)" : "var(--mf-border)",
                      border: "none", cursor: "pointer", position: "relative",
                    }}
                  >
                    <span style={{
                      position: "absolute", top: 3, left: allowDownload ? 23 : 3,
                      width: 20, height: 20, borderRadius: 10, background: "#fff",
                      transition: "left 200ms",
                    }} />
                  </button>
                </div>

                {error && <div style={{ color: "var(--mf-urgent-text)", fontSize: 13, marginBottom: 10 }}>{error}</div>}

                <Button testID="share-create" onClick={submit} loading={busy} icon={<Link2 size={18} />}>
                  Créer le lien
                </Button>
              </>
            )}

            {existingShares.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.5, margin: "24px 0 8px" }}>
                  Liens actifs
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {existingShares.map((s) => (
                    <div key={s.id} data-testid={`existing-share-${s.id}`} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px", borderRadius: 12,
                      background: "var(--mf-bg-muted)",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {s.recipient_label || "Sans destinataire"}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--mf-text-muted)", marginTop: 2 }}>
                          {s.intervention_ids.length} doc{s.intervention_ids.length > 1 ? "s" : ""}
                          {s.expires_at ? ` · Expire ${formatDate(s.expires_at)}` : " · Sans expiration"}
                        </div>
                      </div>
                      <button
                        onClick={() => revoke(s.id)}
                        data-testid={`revoke-share-${s.id}`}
                        aria-label="Révoquer"
                        style={{
                          width: 36, height: 36, borderRadius: 18, border: "none",
                          background: "rgba(239,68,68,0.1)", color: "var(--mf-urgent-solid)",
                          cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          // Created step
          <div data-testid="share-created">
            <div style={{
              padding: 16, background: "var(--mf-bg-muted)", borderRadius: 16, marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, color: "var(--mf-text-secondary)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Lien privé
              </div>
              <div style={{ fontSize: 13, wordBreak: "break-all", fontFamily: "monospace", color: "var(--mf-brand)" }}>
                {generateLink()}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <Button
                variant="secondary"
                fullWidth
                onClick={copyLink}
                icon={copied ? <Check size={16} /> : <Copy size={16} />}
                testID="share-copy-link"
              >
                {copied ? "Copié !" : "Copier"}
              </Button>
              <Button
                fullWidth
                onClick={nativeShare}
                icon={<Share2 size={16} />}
                testID="share-native"
              >
                Partager
              </Button>
            </div>
            <Button
              variant="secondary"
              fullWidth
              icon={<ExternalLink size={16} />}
              onClick={() => window.open(generateLink(), "_blank")}
              testID="share-preview"
            >
              Prévisualiser
            </Button>
            <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 14, lineHeight: 1.5, textAlign: "center" }}>
              Vous pouvez révoquer ce lien à tout moment depuis « Partager des documents ».
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
